/**
 * PlanSelector Component
 * Displays subscription plan options for yacht owners
 */

import React, { useState } from 'react';
import { Check, Zap } from 'lucide-react';
import { billingAPI } from '../services/api';

const PLANS = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 29,
    currency: 'EUR',
    interval: 'month',
    features: [
      'Unlimited mooring requests',
      'Access to all helpers',
      'Real-time chat',
      'Ratings & reviews',
      'Cancel anytime'
    ]
  },
  {
    id: 'yearly',
    name: 'Yearly',
    price: 249,
    currency: 'EUR',
    interval: 'year',
    savings: 'Save 28%',
    features: [
      'Everything in Monthly',
      '2 months FREE',
      'Priority support',
      'Best value'
    ],
    recommended: true
  }
];

function PlanSelector({ onSuccess, onError }) {
  const [loading, setLoading] = useState(null);

  const handleSelectPlan = async (planId) => {
    setLoading(planId);
    try {
      const response = await billingAPI.createCheckoutSession(planId);
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      if (onError) {
        onError(error.message || 'Failed to start checkout');
      }
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        {PLANS.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            loading={loading === plan.id}
            disabled={loading !== null}
            onSelect={() => handleSelectPlan(plan.id)}
          />
        ))}
      </div>
      <p className="text-xs text-center text-navy-500">
        Secure payment powered by Stripe. Cancel anytime.
      </p>
    </div>
  );
}

function PlanCard({ plan, loading, disabled, onSelect }) {
  return (
    <div
      className={`relative bg-white rounded-xl border-2 p-6 transition-all ${
        plan.recommended
          ? 'border-primary-500 shadow-lg'
          : 'border-navy-200 hover:border-navy-300'
      }`}
    >
      {plan.recommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1 bg-primary-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
            <Zap className="h-3 w-3" />
            Best Value
          </span>
        </div>
      )}

      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-navy-900">{plan.name}</h3>
        <div className="mt-2">
          <span className="text-3xl font-bold text-navy-900">{plan.currency === 'EUR' ? '€' : '$'}{plan.price}</span>
          <span className="text-navy-500">/{plan.interval}</span>
        </div>
        {plan.savings && (
          <span className="inline-block mt-2 bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded">
            {plan.savings}
          </span>
        )}
      </div>

      <ul className="space-y-2 mb-6">
        {plan.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2 text-sm text-navy-700">
            <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={onSelect}
        disabled={disabled}
        className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
          plan.recommended
            ? 'bg-primary-600 text-white hover:bg-primary-700 disabled:bg-primary-400'
            : 'bg-navy-100 text-navy-700 hover:bg-navy-200 disabled:bg-navy-50'
        } disabled:cursor-not-allowed`}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <span className="spinner h-4 w-4" />
            Processing...
          </span>
        ) : (
          `Choose ${plan.name}`
        )}
      </button>
    </div>
  );
}

export default PlanSelector;
